import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import {
  GoogleMyBusinessAccount,
  IntegrationChannelType,
  useGetIntegrationEditFormDataQuery,
} from 'generated/graphql';
import { SyntheticEvent, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerFooter } from '../../AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '../../IntegrationDescriptionForm';
import { EditIntegrationIncompleteWarningAlert } from '../EditIntegrationIncompleteWarningAlert';

export const EditGoogleMyBusinessIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationData,
  integrationType,
  description,
  integrationChannel,
}: EditIntegrationDrawerContentProps) => {
  const { showErrorNotification } = useNotificationContext();

  const [selectedAccount, setSelectedAccount] = useState<
    GoogleMyBusinessAccount | undefined
  >();

  const [descriptionInput, setDescriptionInput] = useState(description);

  const { data: editFormData, loading: isLoadingFormData } =
    useGetIntegrationEditFormDataQuery({
      variables: {
        integrationID,
      },
      fetchPolicy: 'cache-and-network',
      onError: () => {
        showErrorNotification({
          contentComponent: 'Failed to fetch integrations form data.',
          props: {},
        });
      },
      onCompleted: (data) => {
        const googleMyBusinessFormData =
          data?.getIntegrationEditFormData?.formData?.googleMyBusiness;

        // Automatically select the only available account
        if (googleMyBusinessFormData?.accounts?.length === 1) {
          setSelectedAccount(googleMyBusinessFormData.accounts[0]);
        }
      },
    });

  const handleChange = (
    _: SyntheticEvent,
    value: GoogleMyBusinessAccount | null,
  ) => {
    if (!value) {
      return;
    }
    setSelectedAccount(value);
  };

  const existingAccount = integrationData.googleMyBusiness?.account;

  const availableAccounts =
    editFormData?.getIntegrationEditFormData?.formData?.googleMyBusiness
      ?.accounts ?? [];

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.GoogleMyBusiness,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.GoogleMyBusiness,
          integrationID,
        });
      },
    });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.GoogleMyBusiness,
    integrationType,
    hasValidFormFields: !existingAccount ? !!selectedAccount : true,
    descriptionInput,
    currentDescription: description,
    onValidated: async () => {
      selectedAccount &&
        updateIntegration({
          integrationID,
          isActive: true,
          updateData: {
            googleMyBusiness: {
              account: {
                id: selectedAccount.id,
                name: selectedAccount.name,
              },
            },
          },
          updateDescription: {
            description: descriptionInput.trim(),
          },
        });
    },
  });

  const isSubmitting = IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title="Edit Google My Business Integration"
        />
        <Box p={3}>
          <EditIntegrationIncompleteWarningAlert
            integrationData={integrationData}
            channelType={IntegrationChannelType.GoogleMyBusiness}
          />
          <Stack
            spacing={4}
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
            autoComplete="off"
          >
            {!existingAccount && (
              <Stack spacing={2}>
                <Stack>
                  <Typography variant="subtitle1">
                    Select Google My Business Account
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select the account you would like Enterpret to get data from
                  </Typography>
                </Stack>
                <Autocomplete
                  fullWidth
                  value={selectedAccount}
                  options={availableAccounts}
                  getOptionLabel={(option) => option.name}
                  onChange={handleChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Google My Business Account" />
                  )}
                  componentsProps={{ paper: { elevation: 8 } }}
                  loading={isLoadingFormData}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              </Stack>
            )}
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          </Stack>
        </Box>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        isDisabled={isDisabled}
        isLoading={isSubmitting}
      />
    </Stack>
  );
};
